import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usersApi, categoriesApi, type UserWithCreds } from '../lib/api-hooks';
import { useAuth } from '../store/auth';
import { clsx } from 'clsx';
import { formatDate } from '../lib/format';

const ROLLE_LABELS: Record<string, string> = {
  ADMIN: 'Administrator',
  AGENT: 'Agent',
  VIEWER: 'Lesetilgang',
};

const ROLLE_COLORS: Record<string, string> = {
  ADMIN: 'bg-brand-100 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300',
  AGENT: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
  VIEWER: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400',
};

export default function UsersPage() {
  const qc = useQueryClient();
  const { user: currentUser } = useAuth();
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingUser, setEditingUser] = useState<UserWithCreds | null>(null);
  const [showCredsFor, setShowCredsFor] = useState<UserWithCreds | null>(null);

  const { data: users, isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: usersApi.list,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { rolle?: string; aktiv?: boolean; navn?: string } }) =>
      usersApi.update(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });

  if (isLoading) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-6 md:px-6">
        <div className="mb-6 h-8 w-40 skeleton" />
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="card h-16 p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full skeleton" />
                <div className="flex-1 space-y-1">
                  <div className="h-3 w-32 skeleton" />
                  <div className="h-2 w-48 skeleton" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const isAdmin = currentUser?.rolle === 'ADMIN';

  return (
    <div className="mx-auto max-w-4xl px-4 py-6 md:px-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between animate-fade-in-down">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">Brukere</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            {users?.length ?? 0} brukere i systemet
          </p>
        </div>
        {isAdmin && (
          <button onClick={() => setShowAddModal(true)} className="btn-primary">
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14" />
              <path d="M5 12h14" />
            </svg>
            <span className="hidden sm:inline">Ny bruker</span>
          </button>
        )}
      </div>

      {/* Bruker-liste */}
      <div className="card animate-fade-in-up overflow-hidden">
        <div className="divide-y divide-slate-100 dark:divide-slate-800">
          {users?.map((u) => (
            <div
              key={u.id}
              className="flex items-center gap-4 px-5 py-4 transition-colors hover:bg-slate-50/50 dark:hover:bg-slate-800/30"
            >
              {/* Avatar */}
              <div className={clsx(
                'flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white',
                u.aktiv ? 'bg-gradient-brand' : 'bg-slate-400',
              )}>
                {u.navn?.charAt(0).toUpperCase() ?? '?'}
              </div>

              {/* Info */}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate font-medium text-slate-800 dark:text-slate-100">{u.navn}</span>
                  {u.id === currentUser?.id && (
                    <span className="chip bg-slate-100 text-xs text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                      Deg
                    </span>
                  )}
                  {!u.aktiv && (
                    <span className="chip bg-red-100 text-xs text-red-600 dark:bg-red-500/15 dark:text-red-400">
                      Inaktiv
                    </span>
                  )}
                </div>
                <div className="truncate text-sm text-slate-500 dark:text-slate-400">{u.epost}</div>
              </div>

              {/* Rolle */}
              <span className={clsx('chip hidden shrink-0 sm:inline-flex', ROLLE_COLORS[u.rolle])}>
                {ROLLE_LABELS[u.rolle] || u.rolle}
              </span>

              {/* Kategori-tilgang (badges) – kun for ikke-admin */}
              {u.rolle !== 'ADMIN' && (u as any).categoryAccess?.length > 0 && (
                <div className="hidden shrink-0 items-center gap-1 lg:flex">
                  {(u as any).categoryAccess.slice(0, 3).map((ca: any) => (
                    <span
                      key={ca.category.id}
                      className="h-2.5 w-2.5 rounded-full"
                      style={{ backgroundColor: ca.category.farge }}
                      title={ca.category.navn}
                    />
                  ))}
                  {(u as any).categoryAccess.length > 3 && (
                    <span className="text-xs text-slate-400">+{(u as any).categoryAccess.length - 3}</span>
                  )}
                </div>
              )}

              {/* Meny-knapp (kun admin) */}
              {isAdmin && (
                <button
                  onClick={() => setEditingUser(u)}
                  className="btn-ghost p-2"
                  title="Rediger bruker"
                >
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4Z" />
                  </svg>
                </button>
              )}
            </div>
          ))}

          {users?.length === 0 && (
            <div className="px-5 py-12 text-center">
              <p className="text-sm text-slate-400">Ingen brukere funnet.</p>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showAddModal && (
        <AddUserModal
          onClose={() => setShowAddModal(false)}
          onCreated={() => {
            setShowAddModal(false);
            qc.invalidateQueries({ queryKey: ['users'] });
          }}
        />
      )}

      {editingUser && (
        <EditUserModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSave={(data) => {
            updateMutation.mutate({ id: editingUser.id, data });
            setEditingUser(null);
          }}
          onShowCreds={() => {
            setShowCredsFor(editingUser);
            setEditingUser(null);
          }}
        />
      )}

      {showCredsFor && (
        <CredentialsModal
          user={showCredsFor}
          onClose={() => setShowCredsFor(null)}
        />
      )}
    </div>
  );
}

// ---------- Legg til bruker ----------
function AddUserModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [navn, setNavn] = useState('');
  const [epost, setEpost] = useState('');
  const [rolle, setRolle] = useState<'AGENT' | 'ADMIN' | 'VIEWER'>('AGENT');
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!navn.trim() || !epost.trim()) return;
    setCreating(true);
    setError(null);
    try {
      await usersApi.create({ navn: navn.trim(), epost: epost.trim(), rolle });
      onCreated();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 animate-fade-in" onClick={onClose}>
      <div className="card animate-scale-in w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Ny bruker</h2>
          <button onClick={onClose} className="btn-ghost p-1">
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6 6 18" /><path d="m6 6 12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">Navn *</label>
            <input
              type="text"
              required
              value={navn}
              onChange={(e) => setNavn(e.target.value)}
              placeholder="Fornavn Etternavn"
              className="input"
              autoFocus
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">E-post *</label>
            <input
              type="email"
              required
              value={epost}
              onChange={(e) => setEpost(e.target.value)}
              placeholder="navn@firma.no"
              className="input"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">Rolle</label>
            <div className="flex gap-1">
              {(['VIEWER', 'AGENT', 'ADMIN'] as const).map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setRolle(r)}
                  className={clsx(
                    'flex-1 rounded-lg px-3 py-2 text-sm font-medium transition-all',
                    rolle === r
                      ? 'bg-gradient-brand text-white shadow-soft'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300',
                  )}
                >
                  {ROLLE_LABELS[r]}
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-xl bg-brand-50/50 p-3 text-xs text-slate-600 dark:bg-brand-500/5 dark:text-slate-400">
            <p>
              <strong>Slik aktiverer brukeren:</strong> Etter opprettelse må brukeren gå til login-siden,
              velge "Registrer nøkkel" og registrere en passkey med samme e-post.
            </p>
          </div>

          {error && (
            <div className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-400">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary">Avbryt</button>
            <button type="submit" className="btn-primary" disabled={creating || !navn.trim() || !epost.trim()}>
              {creating ? 'Oppretter…' : 'Opprett bruker'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------- Rediger bruker ----------
function EditUserModal({
  user,
  onClose,
  onSave,
  onShowCreds,
}: {
  user: UserWithCreds;
  onClose: () => void;
  onSave: (data: { rolle?: string; aktiv?: boolean; navn?: string }) => void;
  onShowCreds: () => void;
}) {
  const qc = useQueryClient();
  const [navn, setNavn] = useState(user.navn);
  const [rolle, setRolle] = useState(user.rolle);
  const [aktiv, setAktiv] = useState(user.aktiv);

  // Kategori-tilgang
  const { data: allCategories } = useQuery({
    queryKey: ['categories'],
    queryFn: categoriesApi.list,
  });

  const { data: userCategories } = useQuery({
    queryKey: ['user-categories', user.id],
    queryFn: () => usersApi.getCategories(user.id),
  });

  const [selectedCatIds, setSelectedCatIds] = useState<string[] | null>(null);

  // Initialiser valgte kategorier når de er lastet
  const effectiveCatIds = selectedCatIds ?? (userCategories?.map((c) => c.id) ?? []);

  const saveCategories = useMutation({
    mutationFn: (categoryIds: string[]) => usersApi.setCategories(user.id, categoryIds),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['user-categories', user.id] });
      qc.invalidateQueries({ queryKey: ['users'] });
    },
  });

  const toggleCategory = (catId: string) => {
    const current = effectiveCatIds.includes(catId)
      ? effectiveCatIds.filter((id) => id !== catId)
      : [...effectiveCatIds, catId];
    setSelectedCatIds(current);
    saveCategories.mutate(current);
  };

  const isAdmin = rolle === 'ADMIN';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 animate-fade-in" onClick={onClose}>
      <div className="card animate-scale-in w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Rediger bruker</h2>
          <button onClick={onClose} className="btn-ghost p-1">
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6 6 18" /><path d="m6 6 12 12" />
            </svg>
          </button>
        </div>

        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-brand text-lg font-semibold text-white">
            {user.navn?.charAt(0).toUpperCase() ?? '?'}
          </div>
          <div>
            <div className="font-medium text-slate-800 dark:text-slate-100">{user.navn}</div>
            <div className="text-sm text-slate-500 dark:text-slate-400">{user.epost}</div>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">Navn</label>
            <input type="text" value={navn} onChange={(e) => setNavn(e.target.value)} className="input" />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">Rolle</label>
            <div className="flex gap-1">
              {(['VIEWER', 'AGENT', 'ADMIN'] as const).map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setRolle(r)}
                  className={clsx(
                    'flex-1 rounded-lg px-3 py-2 text-sm font-medium transition-all',
                    rolle === r
                      ? 'bg-gradient-brand text-white shadow-soft'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300',
                  )}
                >
                  {ROLLE_LABELS[r]}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">Status</label>
            <button
              type="button"
              onClick={() => setAktiv(!aktiv)}
              className={clsx(
                'flex w-full items-center justify-between rounded-xl border p-3 transition-all',
                aktiv
                  ? 'border-green-300 bg-green-50 dark:border-green-500/30 dark:bg-green-500/5'
                  : 'border-red-300 bg-red-50 dark:border-red-500/30 dark:bg-red-500/5',
              )}
            >
              <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
                {aktiv ? 'Aktiv' : 'Deaktivert'}
              </span>
              <div className={clsx(
                'relative h-6 w-11 rounded-full transition-colors',
                aktiv ? 'bg-green-500' : 'bg-slate-400',
              )}>
                <div className={clsx(
                  'absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform',
                  aktiv ? 'translate-x-5' : 'translate-x-0.5',
                )} />
              </div>
            </button>
          </div>

          {/* Kategori-tilgang (kun for ikke-admin) */}
          {!isAdmin && (
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">
                Kategori-tilgang
              </label>
              <p className="mb-2 text-xs text-slate-400">
                Velg hvilke sakstyper denne brukeren skal kunne se i innboksen. Saker tildelt brukeren direkte vises uavhengig av kategori.
              </p>
              <div className="flex flex-wrap gap-2">
                {allCategories?.filter((c) => c.aktiv).map((c) => {
                  const selected = effectiveCatIds.includes(c.id);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => toggleCategory(c.id)}
                      className={clsx(
                        'flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition-all',
                        selected
                          ? 'border-transparent text-white shadow-soft'
                          : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300',
                      )}
                      style={selected ? { backgroundColor: c.farge } : undefined}
                    >
                      <span
                        className={clsx('h-2 w-2 rounded-full', !selected && 'border')}
                        style={!selected ? { borderColor: c.farge } : { backgroundColor: 'rgba(255,255,255,0.5)' }}
                      />
                      {c.navn}
                    </button>
                  );
                })}
                {allCategories?.filter((c) => c.aktiv).length === 0 && (
                  <p className="text-xs text-slate-400">
                    Ingen sakstyper definert enda. Opprett sakstyper under "Sakstyper" i menyen.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Passkeys */}
          <button
            type="button"
            onClick={onShowCreds}
            className="btn-secondary w-full"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="7.5" cy="15.5" r="5.5" />
              <path d="m21 2-9.6 9.6" />
              <path d="m15.5 7.5 3 3L22 7l-3-3" />
            </svg>
            Vis sikkerhetsnøkler
          </button>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary">Avbryt</button>
            <button
              type="button"
              onClick={() => onSave({ navn: navn.trim(), rolle, aktiv })}
              className="btn-primary"
            >
              Lagre
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- Sikkerhetsnøkler ----------
function CredentialsModal({ user, onClose }: { user: UserWithCreds; onClose: () => void }) {
  const qc = useQueryClient();
  const { data: creds, isLoading } = useQuery({
    queryKey: ['user-credentials', user.id],
    queryFn: () => usersApi.listCredentials(user.id),
  });

  const removeMutation = useMutation({
    mutationFn: (credId: string) => usersApi.removeCredential(user.id, credId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['user-credentials', user.id] }),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 animate-fade-in" onClick={onClose}>
      <div className="card animate-scale-in w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Sikkerhetsnøkler</h2>
          <button onClick={onClose} className="btn-ghost p-1">
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6 6 18" /><path d="m6 6 12 12" />
            </svg>
          </button>
        </div>

        <p className="mb-4 text-sm text-slate-500 dark:text-slate-400">
          Passkeys for <strong className="text-slate-700 dark:text-slate-200">{user.navn}</strong> ({user.epost})
        </p>

        {isLoading ? (
          <div className="space-y-2">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="h-16 skeleton" />
            ))}
          </div>
        ) : creds && creds.length > 0 ? (
          <div className="space-y-2">
            {creds.map((c) => (
              <div key={c.id} className="flex items-center gap-3 rounded-xl border border-slate-200 p-3 dark:border-slate-700">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-100 dark:bg-brand-500/15">
                  <svg className="h-5 w-5 text-brand-600 dark:text-brand-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="7.5" cy="15.5" r="5.5" />
                    <path d="m21 2-9.6 9.6" />
                    <path d="m15.5 7.5 3 3L22 7l-3-3" />
                  </svg>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-slate-700 dark:text-slate-200">
                    {c.deviceNavn || 'Uten navn'}
                  </div>
                  <div className="text-xs text-slate-400">
                    Lagt til {formatDate(c.opprettet)}
                    {c.sistBrukt && ` • Sist brukt ${formatDate(c.sistBrukt)}`}
                  </div>
                </div>
                <button
                  onClick={() => removeMutation.mutate(c.id)}
                  className="btn-ghost p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10"
                  title="Slett nøkkel"
                >
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 6h18" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                    <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-xl bg-slate-50 p-6 text-center dark:bg-slate-800/50">
            <p className="text-sm text-slate-400">Ingen sikkerhetsnøkler registrert.</p>
            <p className="mt-1 text-xs text-slate-400">
              Brukeren må registrere en passkey fra login-siden.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
